/* eslint-disable @wyz/module/no-relative-import */
import { TFunction } from '@wyz/types';
import { Alert, Button, FullPageSpinner, Icon, Tooltip } from '@wyz/ui';
import { useBoolean } from '@wyz/utils';
import * as React from 'react';
import { I18nextProvider, TransProps, useTranslation } from 'react-i18next';
import { Trans as BaseTrans } from 'react-i18next/TransWithoutContext';
import { useConfig, useLocale } from '~/common/context';
import { I18n, ROLES } from '~/common/helpers';
import { TranslationContext } from './contexts';

import { useUserAccessControl } from '../hooks';
import './i18n-provider.scss';
import { TranslationKeys } from '~/common/types';

export function I18nProvider(props: React.PropsWithChildren) {
  const [initialized, setInitialized] = React.useState(false);
  const [translateModeEnabled, handlers] = useBoolean();
  const { resolvedLocale } = useLocale();
  const config = useConfig();
  const { isGranted } = useUserAccessControl();
  React.useLayoutEffect(() => {
    I18n.init(resolvedLocale, config)
      .then(() => {
        setInitialized(true);
      })
      .catch(() => {});
  }, [resolvedLocale, config]);
  const instance = I18n.getInstance();
  if (!initialized) {
    return <FullPageSpinner />;
  }

  return (
    <TranslationContext.Provider
      value={{
        translateModeEnabled: translateModeEnabled,
      }}
    >
      {translateModeEnabled && (
        <Alert
          style={{ position: 'fixed', top: 0, width: '100vw', zIndex: 9999 }}
          dismissible
          className='text-center'
        >
          {instance.t('app.common.translator_mode.alert_message')}
        </Alert>
      )}
      <I18nextProvider i18n={instance}>{props.children}</I18nextProvider>
      {isGranted([ROLES.ROLE_LOCO_TRANSLATION]) && (
        <div style={{ position: 'fixed', bottom: 10, left: 10, zIndex: 9999 }}>
          <Tooltip
            label={instance.t('app.common.translator_mode.enable_button')}
          >
            <Button
              id='translate'
              rounded
              variant={translateModeEnabled ? 'success' : 'danger'}
              onClick={handlers.toggle}
              size='lg'
            >
              <Icon icon='globe' size='lg' />
            </Button>
          </Tooltip>
        </div>
      )}
    </TranslationContext.Provider>
  );
}

type TranslationReturn = Omit<
  ReturnType<typeof useTranslation<'messages'>>,
  't'
> & {
  t: TFunction<TranslationKeys>;
};

const useTranslationContext = () => {
  const context = React.useContext(TranslationContext);
  return context || { translateModeEnabled: false };
};

export const useI18n = (): TranslationReturn => {
  return useTranslation(['messages']);
};

export const Trans = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: Omit<TransProps<any>, 't' | 'i18nKey' | 'values'> & {
    i18nKey: keyof TranslationKeys;
    values?: Record<string, unknown>;
  },
) => {
  const { t } = useI18n();
  const { translateModeEnabled } = useTranslationContext();
  if (!translateModeEnabled) {
    // @ts-expect-error TS internal bug: Expression produces a union type that is too complex to represent
    return <BaseTrans {...props} t={t} />;
  }

  return (
    <span>
      <Tooltip label={props.i18nKey}>
        <span className='copy-key position-relative'>
          <Icon
            className='hoverable-btn position-absolute'
            icon='duplicate'
            size='xs'
            onClick={async (event) => {
              await navigator.clipboard.writeText(props.i18nKey);
              event.stopPropagation();
            }}
          />
          {/* @ts-expect-error TS internal bug: Expression produces a union type that is too complex to represent */}
          <BaseTrans {...props} t={t} />
        </span>
      </Tooltip>
    </span>
  );
};
